import { useCallback, useEffect, useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import { EVENTOS_DE_WEBHOOK, EXEMPLO_DE_VERIFICACAO, FALHAS_PARA_ALERTA, rotuloDoEvento, situacaoDaEntrega } from '@/data/webhooks';
import type { DadosDoWebhook, EntregaDeWebhook, EventoDeWebhook, Webhook } from '@/data/webhooks';
import { pode } from '@/session/sessao';

function quando(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
}

const mensagem = (e: unknown, reserva: string) => (e instanceof Error ? e.message : reserva);

const GRUPOS = [...new Set(EVENTOS_DE_WEBHOOK.map((e) => e.grupo))];
const NOVO: DadosDoWebhook = { nome: '', url: 'https://', eventos: [], ativo: true };

const PRE: React.CSSProperties = {
    margin: 0, padding: 12, overflowX: 'auto', background: 'var(--surface-secondary)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', fontSize: 12, lineHeight: '18px',
};

interface Edicao {
    id: string | null;
    dados: DadosDoWebhook;
    original: DadosDoWebhook;
}

function SituacaoDoWebhook({ webhook }: { webhook: Webhook }) {
    let selo;
    if (!webhook.ativo) selo = <Badge tone="neutral">Pausado</Badge>;
    else if (webhook.falhasSeguidas >= FALHAS_PARA_ALERTA) selo = <Badge tone="danger">Falhando</Badge>;
    else if (webhook.falhasSeguidas > 0) selo = <Badge tone="warning">Com falhas</Badge>;
    else selo = <Badge tone="success" dot>Ativo</Badge>;
    return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            {selo}
            {webhook.pendentes > 0 && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{webhook.pendentes} na fila</span>}
        </span>
    );
}

function SituacaoDaEntrega({ entrega }: { entrega: EntregaDeWebhook }) {
    const s = situacaoDaEntrega(entrega);
    if (s === 'entregue') return <Badge tone="success">{`Entregue${entrega.statusHttp ? ` · ${entrega.statusHttp}` : ''}`}</Badge>;
    if (s === 'desistiu') return <Badge tone="danger">Desistiu após 8 tentativas</Badge>;
    if (s === 'na_fila') return <Badge tone="neutral">Na fila</Badge>;
    return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <Badge tone="warning">{`Tentativa ${entrega.tentativas} de 8`}</Badge>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>próxima {quando(entrega.proximaTentativaEm)}</span>
        </span>
    );
}

/**
 * Webhooks de saída (0153): a clínica avisa outros sistemas quando algo acontece.
 *
 * Gerencia quem tem a permissão de configurações (decisão de 15/09/2026). O segredo aparece uma vez,
 * ao criar ou ao gerar outro; a lista mostra só os quatro últimos caracteres.
 */
export function WebhooksDaClinica() {
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const gestor = pode('configuracoes');
    const [lista, setLista] = useState<Webhook[] | null>(null);
    const [erroDaLista, setErroDaLista] = useState<string | null>(null);
    const [edicao, setEdicao] = useState<Edicao | null>(null);
    const [erroDoForm, setErroDoForm] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [segredo, setSegredo] = useState<{ nome: string; valor: string } | null>(null);
    const [entregasDe, setEntregasDe] = useState<Webhook | null>(null);
    const [entregas, setEntregas] = useState<EntregaDeWebhook[] | null>(null);
    const [erroDasEntregas, setErroDasEntregas] = useState<string | null>(null);

    const carregar = useCallback(async () => {
        try {
            setLista(await dados().webhooks());
            setErroDaLista(null);
        } catch (e) {
            setErroDaLista(mensagem(e, 'Não foi possível ler os webhooks.'));
        }
    }, []);

    useEffect(() => {
        if (gestor) void carregar();
    }, [gestor, carregar]);

    const lerEntregas = useCallback(async (w: Webhook) => {
        setEntregas(null);
        setErroDasEntregas(null);
        try {
            setEntregas(await dados().entregasDoWebhook(w.id));
        } catch (e) {
            setErroDasEntregas(mensagem(e, 'Não foi possível ler as entregas.'));
        }
    }, []);

    useEffect(() => {
        if (entregasDe) void lerEntregas(entregasDe);
    }, [entregasDe, lerEntregas]);

    const abrir = (w: Webhook | null) => {
        const d: DadosDoWebhook = w ? { nome: w.nome, url: w.url, eventos: [...w.eventos], ativo: w.ativo } : { ...NOVO, eventos: [] };
        setErroDoForm(null);
        setEdicao({ id: w?.id ?? null, dados: d, original: { ...d, eventos: [...d.eventos] } });
    };

    const alterar = <C extends keyof DadosDoWebhook>(campo: C, valor: DadosDoWebhook[C]) => {
        setErroDoForm(null);
        setEdicao((ed) => (ed ? { ...ed, dados: { ...ed.dados, [campo]: valor } } : ed));
    };

    const alternarEvento = (id: EventoDeWebhook, marcado: boolean) => {
        setErroDoForm(null);
        setEdicao((ed) => (ed ? {
            ...ed,
            dados: { ...ed.dados, eventos: marcado ? [...new Set([...ed.dados.eventos, id])] : ed.dados.eventos.filter((x) => x !== id) },
        } : ed));
    };

    const sujo = edicao !== null && JSON.stringify({ ...edicao.dados, eventos: [...edicao.dados.eventos].sort() })
        !== JSON.stringify({ ...edicao.original, eventos: [...edicao.original.eventos].sort() });
    const preenchido = edicao !== null && edicao.dados.nome.trim().length >= 3
        && /^https:\/\/\S+\.\S+/.test(edicao.dados.url.trim()) && edicao.dados.eventos.length > 0;

    const salvar = async (): Promise<boolean> => {
        if (!edicao) return true;
        setSalvando(true);
        setErroDoForm(null);
        try {
            if (edicao.id) {
                setLista(await dados().atualizarWebhook(edicao.id, edicao.dados));
                aviso({ title: 'Webhook salvo', description: edicao.dados.nome });
            } else {
                const criado = await dados().criarWebhook(edicao.dados);
                setSegredo({ nome: criado.webhook.nome, valor: criado.segredo });
                await carregar();
            }
            setEdicao(null);
            return true;
        } catch (e) {
            setErroDoForm(mensagem(e, 'Não foi possível salvar o webhook.'));
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const girar = async () => {
        if (!edicao?.id) return;
        const ok = await confirmar({
            titulo: 'Gerar um novo segredo?',
            descricao: 'O segredo atual para de valer na hora. Atualize o outro sistema com o novo, senão ele vai recusar as próximas entregas.',
            confirmar: 'Gerar novo segredo',
        });
        if (!ok) return;
        try {
            const valor = await dados().girarSegredoDoWebhook(edicao.id);
            setSegredo({ nome: edicao.dados.nome, valor });
            setEdicao(null);
            await carregar();
        } catch (e) {
            setErroDoForm(mensagem(e, 'Não foi possível gerar o novo segredo.'));
        }
    };

    const excluir = async () => {
        if (!edicao?.id) return;
        const nome = edicao.original.nome;
        const ok = await confirmar({
            titulo: `Excluir o webhook ${nome}?`,
            descricao: 'As entregas na fila são descartadas e o histórico some. Não dá para desfazer.',
            confirmar: 'Excluir webhook',
        });
        if (!ok) return;
        try {
            setLista(await dados().excluirWebhook(edicao.id));
            setEdicao(null);
            aviso({ title: 'Webhook excluído', description: nome });
        } catch (e) {
            setErroDoForm(mensagem(e, 'Não foi possível excluir o webhook.'));
        }
    };

    const testar = async (w: Webhook) => {
        try {
            await dados().testarWebhook(w.id);
            aviso({ title: 'Teste na fila', description: `${w.nome} recebe em até 30 segundos. O resultado aparece em Entregas.` });
            await carregar();
        } catch (e) {
            aviso({ title: 'Não foi possível enviar o teste', description: mensagem(e, 'Tente de novo.'), tone: 'danger' });
        }
    };

    const reenviar = async (e: EntregaDeWebhook) => {
        if (!entregasDe) return;
        try {
            await dados().reenviarEntregaDeWebhook(e.id);
            aviso({ title: 'Entrega reenviada', description: 'Sai em até 30 segundos, como uma entrega nova.' });
            await lerEntregas(entregasDe);
        } catch (err) {
            aviso({ title: 'Não foi possível reenviar', description: mensagem(err, 'Tente de novo.'), tone: 'danger' });
        }
    };

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: 'Segredo copiado' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: 'Selecione o segredo e copie à mão.', tone: 'warning' });
        }
    };

    const colunas: DataTableColumn<Webhook>[] = [
        {
            key: 'nome', header: 'Webhook',
            render: (w) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{w.nome}</strong>
                    <code style={{ fontSize: 12, color: 'var(--text-tertiary)', wordBreak: 'break-all' }}>{w.url}</code>
                </span>
            ),
        },
        {
            key: 'eventos', header: 'Eventos', width: 220,
            render: (w) => (w.eventos.length === EVENTOS_DE_WEBHOOK.length ? 'Todos os eventos' : w.eventos.map(rotuloDoEvento).join(', ')),
        },
        { key: 'situacao', header: 'Situação', width: 130, render: (w) => <SituacaoDoWebhook webhook={w} /> },
        { key: 'ultimaEntregaEm', header: 'Última entrega', width: 150, render: (w) => (w.ultimaEntregaEm ? quando(w.ultimaEntregaEm) : 'nenhuma ainda') },
        {
            key: 'acoes', header: '', align: 'right', width: 240,
            render: (w) => (
                <span style={{ display: 'inline-flex', gap: 2 }}>
                    <Button size="sm" variant="ghost" onClick={() => setEntregasDe(w)} aria-label={`Entregas de ${w.nome}`}>Entregas</Button>
                    <Button size="sm" variant="ghost" disabled={!w.ativo} onClick={() => { void testar(w); }} aria-label={`Testar ${w.nome}`}>Testar</Button>
                    <Button size="sm" variant="ghost" onClick={() => abrir(w)} aria-label={`Editar ${w.nome}`}>Editar</Button>
                </span>
            ),
        },
    ];

    const colunasDasEntregas: DataTableColumn<EntregaDeWebhook>[] = [
        {
            key: 'evento', header: 'Evento', width: 190,
            render: (e) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{rotuloDoEvento(e.evento)}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{quando(e.criadoEm)}</span>
                </span>
            ),
        },
        { key: 'situacao', header: 'Situação', width: 190, render: (e) => <SituacaoDaEntrega entrega={e} /> },
        {
            key: 'detalhe', header: 'Detalhe',
            render: (e) => (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {e.erro && <span style={{ color: 'var(--text-secondary)' }}>{e.erro}</span>}
                    <details>
                        <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--text-link)' }}>Ver os dados enviados</summary>
                        <pre style={{ ...PRE, marginTop: 6 }}>{JSON.stringify(e.carga, null, 2)}</pre>
                    </details>
                </span>
            ),
        },
        {
            key: 'acoes', header: '', align: 'right', width: 100,
            render: (e) => {
                const s = situacaoDaEntrega(e);
                return s === 'entregue' || s === 'desistiu'
                    ? <Button size="sm" variant="ghost" onClick={() => { void reenviar(e); }}>Reenviar</Button>
                    : null;
            },
        },
    ];

    return (
        <Card
            title="Webhooks"
            subtitle="Avise outros sistemas — n8n, Zapier, um ERP — quando algo acontece na clínica"
            padding={18}
            actions={gestor ? (
                <Button size="sm" iconLeft={<Icon name="webhook" size={14} />} onClick={() => abrir(null)}>Novo webhook</Button>
            ) : undefined}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, lineHeight: '19px' }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                    A cada evento marcado, o endereço recebe um <code>POST</code> em JSON com <code>id</code>, <code>evento</code>,{' '}
                    <code>ocorrido_em</code>, <code>clinica_id</code> e <code>dados</code>, assinado no padrão Standard Webhooks
                    (cabeçalho <code>webhook-signature</code>). Sem resposta 2xx em 10 segundos, tentamos de novo em 1, 2, 4… minutos,
                    até 8 vezes.
                </p>

                {!gestor && (
                    <Alert tone="neutral" dismissible={false} title="Só quem configura a clínica gerencia webhooks">
                        Um webhook manda dados da clínica para outro sistema: fica com quem responde pelas configurações.
                    </Alert>
                )}
                {erroDaLista && <Alert tone="danger" dismissible={false}>{erroDaLista}</Alert>}

                {gestor && (
                    <DataTable
                        columns={colunas} rows={lista ?? []} rowKey="id" stickyHeader={false}
                        emptyMessage={lista === null ? 'Lendo os webhooks…' : 'Nenhum webhook criado.'}
                    />
                )}
            </div>

            <Modal
                open={edicao !== null} onClose={() => setEdicao(null)} width={620}
                title={edicao?.id ? 'Editar webhook' : 'Novo webhook'}
                description="O endereço recebe um POST assinado a cada evento marcado."
                dirty={sujo} onSave={salvar}
                footer={(
                    <>
                        {edicao?.id && (
                            <>
                                <Button variant="ghost" onClick={() => { void excluir(); }}>Excluir</Button>
                                <Button variant="ghost" onClick={() => { void girar(); }}>Gerar novo segredo</Button>
                                <span style={{ marginLeft: 'auto' }} />
                            </>
                        )}
                        <Button variant="secondary" onClick={() => setEdicao(null)}>Cancelar</Button>
                        <Button loading={salvando} disabled={!preenchido || (edicao?.id !== null && !sujo)} onClick={() => { void salvar(); }}>
                            {edicao?.id ? 'Salvar' : 'Criar webhook'}
                        </Button>
                    </>
                )}
            >
                {edicao && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {erroDoForm && <Alert tone="danger" dismissible={false}>{erroDoForm}</Alert>}
                        <FormField label="Nome" htmlFor="webhook-nome" required hint="Ex.: n8n da recepção.">
                            <Input id="webhook-nome" value={edicao.dados.nome} onChange={(v) => alterar('nome', v)} />
                        </FormField>
                        <FormField label="Endereço" htmlFor="webhook-url" required hint="Só https://, num endereço que a internet alcance.">
                            <Input id="webhook-url" type="url" value={edicao.dados.url} onChange={(v) => alterar('url', v)} placeholder="https://seu-n8n.com.br/webhook/…" />
                        </FormField>
                        <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <legend style={{ padding: 0, marginBottom: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Eventos</legend>
                            {GRUPOS.map((grupo) => (
                                <div key={grupo} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{grupo}</span>
                                    {EVENTOS_DE_WEBHOOK.filter((e) => e.grupo === grupo).map((e) => (
                                        <Checkbox
                                            key={e.id} checked={edicao.dados.eventos.includes(e.id)} onChange={(v) => alternarEvento(e.id, v)}
                                            label={e.rotulo} description={e.descricao}
                                        />
                                    ))}
                                </div>
                            ))}
                        </fieldset>
                        {edicao.id && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                    Pausado, o webhook guarda os eventos na fila e só envia quando voltar a ficar ativo.
                                </span>
                                <Switch checked={edicao.dados.ativo} ariaLabel="Webhook ativo" onChange={(v) => alterar('ativo', v)} />
                            </div>
                        )}
                        <Alert tone="neutral" dismissible={false} title="O que sai da clínica">
                            Cada evento leva o nome, o telefone e o e-mail do contato — nunca prontuário, ficha ou observação interna.
                            O sistema de destino passa a tratar esses dados: cadastre só destinos em que a clínica confia.
                        </Alert>
                    </div>
                )}
            </Modal>

            <Modal
                open={segredo !== null} onClose={() => setSegredo(null)} width={620} title="Copie o segredo agora"
                description={segredo?.nome ?? ''}
                footer={<Button onClick={() => setSegredo(null)}>Já copiei</Button>}
            >
                {segredo && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                        <Alert tone="warning" dismissible={false} title="Esta é a única vez que ele aparece">
                            O outro sistema usa este segredo para conferir que a entrega veio da clínica. Perdeu? Gere outro em Editar.
                        </Alert>
                        <code style={{ display: 'block', padding: 12, wordBreak: 'break-all', background: 'var(--surface-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                            {segredo.valor}
                        </code>
                        <Button variant="secondary" iconLeft={<Icon name="copy" size={14} />} onClick={() => { void copiar(segredo.valor); }}>Copiar</Button>
                        <details>
                            <summary style={{ cursor: 'pointer', color: 'var(--text-link)', fontWeight: 600 }}>Como conferir a assinatura</summary>
                            <pre style={{ ...PRE, marginTop: 8 }}>{EXEMPLO_DE_VERIFICACAO}</pre>
                        </details>
                    </div>
                )}
            </Modal>

            <Modal
                open={entregasDe !== null} onClose={() => setEntregasDe(null)} width={860} title="Entregas"
                description={entregasDe ? `${entregasDe.nome} · as 50 mais recentes; concluídas somem depois de 30 dias` : ''}
                footer={<Button variant="secondary" onClick={() => setEntregasDe(null)}>Fechar</Button>}
            >
                {erroDasEntregas && <Alert tone="danger" dismissible={false}>{erroDasEntregas}</Alert>}
                {!erroDasEntregas && (
                    <DataTable
                        columns={colunasDasEntregas} rows={entregas ?? []} rowKey="id" stickyHeader={false}
                        emptyMessage={entregas === null ? 'Lendo as entregas…' : 'Nenhuma entrega ainda. Use Testar para mandar uma.'}
                    />
                )}
            </Modal>

            {confirmacao}
        </Card>
    );
}
